/**
 * KML, hazard and landholder data on the map (phase 2b.6, docs/specs/map.md §4).
 *
 * The shapes mirror `teslyn_core::planning::kml` (`KmlDocument`, `Folder`, `Placemark`, `Geometry`) and
 * `teslyn_core::planning::hazards` (`HazardSet`, `HazardPin`), as the `kml_read` and `hazard_kml_read` commands
 * return them. Text from a file is data, never markup: every string reaches the screen through `textContent` or
 * `escapeHtml` (inventory 04 §2 item 7, 05 §6 item 7).
 *
 * Landholder parcels (the Diurnal "pinned properties", inventory 05 F5) follow MB's Q56 (decision 83): boundaries
 * may be shared with clients per project; contacts, notes and flags are internal only; only managers may export
 * contacts (`canViewLandholderField`, `landholderForExport` in hazards.ts).
 */
import { canViewLandholderField, type HazardGeneratedWith, type TenantRole } from './hazards.js';
import type { LatLng } from './planning.js';

export type KmlGeometry =
  | { readonly type: 'point'; readonly at: LatLng }
  | { readonly type: 'line_string'; readonly points: readonly LatLng[] }
  | {
      readonly type: 'polygon';
      readonly outer: readonly LatLng[];
      readonly holes: ReadonlyArray<readonly LatLng[]>;
    }
  | { readonly type: 'multi'; readonly parts: readonly KmlGeometry[] };

export interface KmlStyle {
  readonly line_color: string | null;
  readonly line_width: number | null;
  readonly poly_color: string | null;
  readonly icon_color: string | null;
  readonly icon_href: string | null;
  readonly icon_scale: number | null;
  readonly label_scale: number | null;
}

export interface KmlPlacemark {
  readonly name: string;
  readonly description: string;
  readonly style_url: string | null;
  readonly style: KmlStyle | null;
  readonly visible: boolean;
  readonly geometry: KmlGeometry | null;
  readonly extended_data: ReadonlyArray<readonly [string, string]>;
}

export interface KmlFolder {
  readonly name: string;
  readonly description: string;
  readonly visible: boolean;
  readonly folders: readonly KmlFolder[];
  readonly placemarks: readonly KmlPlacemark[];
}

export interface KmlDocument {
  readonly root: KmlFolder;
  readonly styles: Readonly<Record<string, KmlStyle>>;
  readonly extended_data: ReadonlyArray<readonly [string, string]>;
}

export interface HazardPin {
  readonly id: number;
  readonly name: string;
  readonly lat: number;
  readonly lng: number;
  readonly description: string;
  readonly icon_type: string;
  readonly color: string;
  readonly visible: boolean;
}

export interface HazardSet {
  readonly name: string;
  readonly folders: readonly KmlFolder[];
  readonly bnd_file_name: string;
  readonly bnd_kml_text: string;
  readonly boundary: readonly LatLng[];
  readonly pins: readonly HazardPin[];
  /** What the file records of its making (Teslyn Field from 2026.10.10); null or absent for older files. */
  readonly generated_with?: HazardGeneratedWith | null;
}

/** Every coordinate of a geometry. */
export function geometryPoints(g: KmlGeometry | null): LatLng[] {
  if (!g) return [];
  switch (g.type) {
    case 'point':
      return [g.at];
    case 'line_string':
      return [...g.points];
    case 'polygon':
      return [...g.outer, ...g.holes.flat()];
    case 'multi':
      return g.parts.flatMap(geometryPoints);
  }
}

/** The style a placemark uses: its own, else its `styleUrl`'s shared style. */
export function styleOf(
  doc: { readonly styles: Readonly<Record<string, KmlStyle>> },
  p: KmlPlacemark,
): KmlStyle | null {
  if (p.style) return p.style;
  if (!p.style_url) return null;
  return doc.styles[p.style_url.replace(/^#/, '')] ?? null;
}

/**
 * A KML `aabbggrr` colour as a CSS `rgba()` string (data colours from a file, not brand colours), or null when the
 * text is not 8 hex digits.
 */
export function kmlColourToCss(kml: string | null | undefined): string | null {
  if (!kml || !/^[0-9a-fA-F]{8}$/.test(kml.trim())) return null;
  const h = kml.trim();
  const byte = (i: number): number => Number.parseInt(h.slice(i, i + 2), 16);
  const a = byte(0) / 255;
  return `rgba(${byte(6)}, ${byte(4)}, ${byte(2)}, ${Math.round(a * 1000) / 1000})`;
}

// Landholders

/** A landholder parcel (the Diurnal "pinned property"), project data kept with the project's map layers. */
export interface LandParcel {
  readonly id: string;
  readonly name: string;
  /** Where it came from: the KML file name, or the state layer. */
  readonly source: string;
  readonly folder: string;
  readonly geometry: KmlGeometry;
  readonly notes: string;
  readonly needs_notification: boolean;
  readonly objection: boolean;
  /** Owner and contact lines (personal information: internal only, Q56). */
  readonly contacts: readonly string[];
  /** Other imported attributes (ExtendedData). */
  readonly attributes: ReadonlyArray<readonly [string, string]>;
  readonly visible: boolean;
}

/** ExtendedData keys that hold owner or contact details (the Diurnal popup's REGISTERED/OWNER, CONTACT, PHONE keys). */
const CONTACT_KEY =
  /^(registered|owner|owners|contact|contact_name|phone|phone_numb|mobile|mobile_num|email)/i;

/**
 * Notes and flags written by a landholder KML export (`Notes:` and `Flags:` lines, inventory 05 F5). Teslyn writes a
 * `Notes:` line for each line of the notes; they are read back joined, so notes of several lines come back whole.
 */
export function notesAndFlags(description: string): {
  notes: string;
  needs_notification: boolean;
  objection: boolean;
} {
  const notes: string[] = [];
  let needs_notification = false;
  let objection = false;
  for (const line of description.split(/\r?\n/)) {
    const t = line.trim();
    if (/^notes:/i.test(t)) notes.push(t.replace(/^notes:\s*/i, ''));
    if (/^flags:/i.test(t)) {
      needs_notification = /notification/i.test(t);
      objection = /objection/i.test(t);
    }
  }
  return { notes: notes.join('\n'), needs_notification, objection };
}

/** The name of the document a landholder export writes (the Diurnal tool's and Teslyn's, am:24287). */
export const LANDHOLDER_EXPORT_DOCUMENT = 'Pinned Properties';

function isArea(g: KmlGeometry | null): g is KmlGeometry {
  if (!g) return false;
  if (g.type === 'polygon') return g.outer.length >= 4;
  if (g.type === 'multi') return g.parts.some(isArea);
  return false;
}

/**
 * Landholder parcels from a KML or KMZ document: one per placemark with an area (polygons, multi-polygons); points
 * and lines are counted as skipped. Folder names nest as "A / B". Identical geometry is imported once. A landholder
 * export ("Pinned Properties", the Diurnal tool's or Teslyn's) gives each its source back from the description's
 * first line, as the tool reads it (am:24546); any other file is the source itself.
 */
export function landParcelsFromKml(
  doc: KmlDocument,
  source: string,
  existing: readonly LandParcel[] = [],
): { readonly parcels: LandParcel[]; readonly skipped: number; readonly duplicates: number } {
  const seen = new Set(existing.map((p) => JSON.stringify(p.geometry)));
  const exported = doc.root.name.trim() === LANDHOLDER_EXPORT_DOCUMENT;
  const parcels: LandParcel[] = [];
  let skipped = 0;
  let duplicates = 0;
  let n = existing.length;
  const walk = (f: KmlFolder, path: string): void => {
    for (const p of f.placemarks) {
      if (!isArea(p.geometry)) {
        skipped += 1;
        continue;
      }
      const key = JSON.stringify(p.geometry);
      if (seen.has(key)) {
        duplicates += 1;
        continue;
      }
      seen.add(key);
      n += 1;
      const contacts = p.extended_data.filter(([k, v]) => CONTACT_KEY.test(k) && v.trim() !== '');
      const attributes = p.extended_data.filter(([k]) => !CONTACT_KEY.test(k));
      const first = p.description.split(/\r?\n/)[0]?.trim() ?? '';
      parcels.push({
        id: `parcel-${n}`,
        name: p.name.trim() || `Parcel ${n}`,
        source: exported && first && !/^(notes|flags):/i.test(first) ? first : source,
        folder: path,
        geometry: p.geometry,
        ...notesAndFlags(p.description),
        contacts: contacts.map(([k, v]) => `${k}: ${v}`),
        attributes,
        visible: true,
      });
    }
    for (const sub of f.folders) walk(sub, path ? `${path} / ${sub.name}` : sub.name);
  };
  walk(doc.root, '');
  return { parcels, skipped, duplicates };
}

/** One row of an identify popup (rendered as text, never markup). */
export interface IdentifyRow {
  readonly label: string;
  readonly value: string;
}

/** The identify rows of a parcel the viewer may see (Q56). */
export function landParcelIdentify(
  parcel: LandParcel,
  roles: readonly TenantRole[],
  boundariesSharedWithClient: boolean,
): IdentifyRow[] {
  const may = (f: Parameters<typeof canViewLandholderField>[1]): boolean =>
    canViewLandholderField(roles, f, boundariesSharedWithClient);
  const rows: IdentifyRow[] = [];
  if (may('name')) rows.push({ label: 'Property', value: parcel.name });
  if (may('boundary')) {
    if (parcel.folder) rows.push({ label: 'Folder', value: parcel.folder });
    rows.push({ label: 'Source', value: parcel.source });
  }
  if (may('contacts')) for (const c of parcel.contacts) rows.push({ label: 'Contact', value: c });
  if (may('flags')) {
    if (parcel.needs_notification) rows.push({ label: 'Flag', value: 'Needs notification' });
    if (parcel.objection) rows.push({ label: 'Flag', value: 'Objection' });
  }
  if (may('notes') && parcel.notes) rows.push({ label: 'Notes', value: parcel.notes });
  if (may('contacts')) for (const [k, v] of parcel.attributes) rows.push({ label: k, value: v });
  return rows;
}

/** A landholder parcel with a concern: an objection, a notification needed, or notes (inventory 05 F5). */
export function hasConcern(p: LandParcel): boolean {
  return p.objection || p.needs_notification || p.notes.trim() !== '';
}
